/**
 * @description Nav component
 * @file
 * @copyright © Copyright IBI Group., created 2019. All use, disclosure, and/or reproduction of this material is prohibited unless authorized in writing. All rights reserved.
 * @module components/Nav
 */

import React from 'react';
import PropTypes from 'prop-types';

import MultiSelectBox from './MultiSelectBox';
import CONSTANTS from '../config/Constants';

/**
 * @description The Nav component.
 * @class
 * @extends {React.Component}
 */
class Nav extends React.Component {
    
    /**
     * @inheritdoc
     */
    constructor(props) {
        super(props);

        this.state = {
            routeSelections: JSON.parse(
                JSON.stringify(this.props.routeSelections)
            ),
            selectedDate: this.props.selectedDate.toString()
        };
    }

    /**
     * @inheritdoc
     */
    render() {
        const date = new Date();

        return (
            <header className='Nav'>
                <a href={process.env.HOST_SITE_URL}>
                    <div className="Nav-logo"></div>
                </a>
                <h1 className="Nav-title">
                    Journey Time Heatmap{' '}
                    {this.props.environmentName &&
                        this.props.environmentName !== 'Production' && (
                            <div className='Nav-title-environment'>
                                ({this.props.environmentName})
                            </div>
                        )}
                </h1>
                <form
                    className='Nav-data-select-form'
                    onSubmit={this.handleFormSubmit.bind(this)}
                >
                    <MultiSelectBox
                        options={this.state.routeSelections.map(
                            ({ carriageways, ...rest }) => {
                                return {
                                    ...rest,
                                    options: carriageways
                                };
                            }
                        )}
                        onToggleOption={this.handleRouteToggle.bind(this)}
                    ></MultiSelectBox>
                    <span className='Nav-datepicker'>
                        <input
                            value={this.state.selectedDate}
                            onChange={this.handleDateChange.bind(this)}
                            type='date'
                            max={`${date.toISOString().split('T')[0]}`}
                            aria-label='Selected date'
                        />
                    </span>
                    <button
                        className='Nav-data-select-submit'
                        type='submit'
                        aria-label='Submit form'
                    >
                        Apply
                    </button>
                    <button
                        className='Nav-data-reset'
                        onClick={this.handleResetClick.bind(this)}
                        type='button'
                        aria-label='reset form'
                    >
                        Reset
                    </button>
                </form>
            </header>
        );
    }

    /**
     * @inheritdoc
     */
    shouldComponentUpdate(nextProps, nextState) {
        return (
            this.props.showMenu !== nextProps.showMenu ||
            this.props.routeSelections !== nextProps.routeSelections ||
            this.state.routeSelections !== nextState.routeSelections ||
            this.props.selectedDate !== nextProps.selectedDate ||
            this.state.selectedDate !== nextState.selectedDate
        );
    }

    /**
     * @inheritdoc
     */
    componentDidUpdate(prevProps) {
        const state = {};

        if (prevProps.routeSelections !== this.props.routeSelections) {
            state.routeSelections = JSON.parse(
                JSON.stringify(this.props.routeSelections)
            );
        }

        if (prevProps.selectedDate !== this.props.selectedDate) {
            state.selectedDate = this.props.selectedDate.toString();
        }

        Object.keys(state).length > 0 && this.setState(state);
    }

    /**
     * @description Handles the toggling of a route.
     * @param {string} id - The route ID.
     */
    handleRouteToggle(id) {
        const splitId = id.toString().split('_'),
            routeName = splitId[0],
            directionId = splitId[1] && parseInt(splitId[1], 10),
            routeSelections = JSON.parse(
                JSON.stringify(this.state.routeSelections)
            ),
            selectedRoute = routeSelections.find(
                route => route.name === routeName
            );

        if (selectedRoute && directionId) {
            const selectedCarriageway = selectedRoute.carriageways.find(
                carriageway => carriageway.id === id
            );

            if (selectedCarriageway) {
                selectedCarriageway.isSelected = !selectedCarriageway.isSelected;

                selectedRoute.isSelected = selectedRoute.carriageways.every(
                    carriageway => carriageway.isSelected
                )
                    ? CONSTANTS.ROUTE_SELECTION_STATE.complete
                    : selectedRoute.carriageways.some(
                          carriageway => carriageway.isSelected
                      )
                    ? CONSTANTS.ROUTE_SELECTION_STATE.partial
                    : CONSTANTS.ROUTE_SELECTION_STATE.deselected;
            }
        } else if (selectedRoute) {
            selectedRoute.isSelected =
                selectedRoute.isSelected ===
                CONSTANTS.ROUTE_SELECTION_STATE.complete
                    ? CONSTANTS.ROUTE_SELECTION_STATE.deselected
                    : CONSTANTS.ROUTE_SELECTION_STATE.complete;

            selectedRoute.carriageways = selectedRoute.carriageways.map(
                carriageway => {
                    return {
                        ...carriageway,
                        isSelected:
                            selectedRoute.isSelected ===
                            CONSTANTS.ROUTE_SELECTION_STATE.complete
                    };
                }
            );
        }

        this.setState({
            routeSelections: routeSelections
        });
    }

    /**
     * @description Sets the selectedDate state parameter on changing the input value.
     * @param {Object} event - The event.
     */
    handleDateChange(event) {
        this.setState({ selectedDate: event.target.value });
    }

    /**
     * @description Submits the form.
     * @param {Object} event - The submit event.
     */
    handleFormSubmit(event) {
        event.preventDefault();
        this.props.onChangeRouteOptions(
            this.state.routeSelections,
            this.state.selectedDate
        );
    }

    /**
     * @description Handles the reset click event.
     */
    handleResetClick() {
        this.props.onResetFilters();
    }
}

Nav.defaultProps = {
    routeOptions: [],
    onToggleRoute: () => null,
    selectedDate: 'yyyy-mm-dd',
    onChangeDate: () => null,
    showMenu: false,
    toggleMenu: () => null,
    onChangeRouteOptions: () => null,
    onResetFilters: () => null
};

Nav.propTypes = {
    environmentName: PropTypes.string,
    routeSelections: PropTypes.array.isRequired,
    onToggleRoute: PropTypes.func.isRequired,
    selectedDate: PropTypes.string.isRequired,
    onChangeDate: PropTypes.func.isRequired,
    showMenu: PropTypes.bool.isRequired,
    toggleMenu: PropTypes.func.isRequired,
    onChangeRouteOptions: PropTypes.func.isRequired,
    onResetFilters: PropTypes.func.isRequired
};

export default Nav;
